import * as React from "react";
import Image from "next/image";
import Sample from "@public/assets/sample.png";
import { Swiper, SwiperSlide } from "swiper/react";
// import { Scrollbar, A11y } from "swiper";
import { v4 as uuidv4 } from "uuid";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import Link from "next/link";
import Icon from "@components/icon";
import { Currency } from "@graphql/types";
import blurDataUrl from "@utils/shimmer";

type Props = {
  priority: boolean;
  item: {
    __typename?: "VendorService";
    id: any;
    vendors_id: any;
    vendor_service_category_id: any;
    service_name: string;
    pinned: boolean;
    description?: string | null;
    phone_number?: string | null;
    rating: number;
    email?: string | null;
    address?: Array<string | null> | null;
    price: {
      __typename?: "VendorServicePrice";
      currency: Currency;
      amount?: number | null;
    };

    category?: {
      __typename?: "InawoVendorServiceCategory";
      Name: string;
    } | null;
    showcase?: Array<{
      __typename?: "VendorServiceShowcase";
      id: any;
      media: string;
      name?: string | null;
      vendor_service_id: any;
      vendors_id: any;
      vendor_service_category_id: any;
    } | null> | null;
  } | null;
};

const ServiceCarouselWithDescription: React.FC<Props> = (props) => {
  const [show, setShow] = React.useState(false);

  React.useEffect(() => {
    setShow(true);
  }, []);

  return (
    <div className="w-full mt-2 relative">
      <div className="flex min-w-[110px] absolute z-[4] top-0 left-[-2px] p-4 bg-gray-200/[0.5] backdrop-blur-md border border-transparent rounded-tl-[15px] rounded-tr-[5px] rounded-br-[5px] rounded-bl-[15px] overflow-hidden justify-center items-center gap-1 w-1/3 sm:w-1/4 md:!w-[23%] h-36 sm:h-52 md:h-72">
        <div className="flex justify-between flex-col w-full h-full items-start gap-1">
          <div className="flex w-full justify-start gap-6 items-start flex-col">
            <Link href={`/profile/${props.item?.id}`}>
              <a className="font-semibold text-inawo-green w-full line-clamp-2 text-sm sm:text-lg md:text-2xl lg:text-3xl">
                {props.item?.service_name}
              </a>
            </Link>

            <p className=" font-light hidden w-full md:line-clamp-3 text-sm text-inawo-black">
              {props.item?.description ?? ""}
            </p>
          </div>

          <div className="flex flex-wrap gap-1 xs:gap-2 justify-between items-center w-full">
            <div className="flex flex-col items-start justify-center">
              <p className="font-sfpro font-semibold text-xs sm:text-sm md:text-md lg:text-base">
                Starts at
              </p>
              <p className="uppercase text-inawo-black font-sfpro text-sm sm:text-md md:text-lg lg:text-2xl">
                {`${
                  props.item?.price.currency ? props.item.price.currency : ""
                }${Number(props.item?.price.amount).toLocaleString()}`}
              </p>
            </div>

            <div className="flex items-center gap-2 justify-between w-full md:w-auto">
              <div className="w-8 sm:w-12">
                <CircularProgressbar
                  value={props.item?.rating ? props.item.rating * 20 : 0}
                  text={props.item?.rating ? `${props.item.rating}` : "0"}
                  styles={buildStyles({
                    textColor: "#273F3B",
                    pathColor: "#CBB915",
                    trailColor: "#DBDFF1",
                    textSize: "36px",
                  })}
                />
              </div>
              {/*<div className="w-9 h-9 cursor-pointer">
                    <Icon iconName="pinGreen" />
										</div>*/}
            </div>
          </div>
        </div>
      </div>
      {show ? (
        <Swiper
          spaceBetween={8}
          slidesPerView={2.8}
          // modules={[Scrollbar, A11y]}
          // scrollbar={{ draggable: true }}
          grabCursor
          breakpoints={{
            // when window width is >= 320px
            320: {
              slidesPerView: 2.8,
              spaceBetween: 8,
            },
            // when window width is >= 480px
            480: {
              slidesPerView: 3.4,
              spaceBetween: 10,
            },
            // when window width is >= 640px
            640: {
              slidesPerView: 3.8,
              spaceBetween: 15,
            },
            789: {
              slidesPerView: 4.2,
              spaceBetween: 20,
            },
            1280: {
              slidesPerView: 4.2,
              spaceBetween: 20,
            },
            1440: {
              slidesPerView: 6.0,
              spaceBetween: 25,
            },
            1840: {
              slidesPerView: 6.9,
              spaceBetween: 25,
            },
            2200: {
              slidesPerView: 7.8,
              spaceBetween: 25,
            },
          }}
        >
          <SwiperSlide style={{ marginRight: "30px" }}>
            <div className="flex rounded-[10px] overflow-hidden justify-center items-center gap-1 relative h-32 sm:h-48 md:h-64 my-2 md:my-4" />
          </SwiperSlide>

          {!!props.item?.showcase &&
            props.item?.showcase.map((item) => (
              <SwiperSlide key={uuidv4()}>
                <div className="flex bg-inawo-green rounded-[15px] overflow-hidden flex-col justify-start items-start gap-2 relative h-32 sm:h-48 md:h-64 my-2 md:my-4">
                  {item?.media ? (
                    <Image
                      src={item?.media}
                      placeholder="blur"
                      objectFit="cover"
                      objectPosition="center"
                      layout="fill"
                      priority={props.priority}
                      quality={75}
                      sizes="30vw"
                      blurDataURL={blurDataUrl(400, 400)}
                      alt={`category service`}
                    />
                  ) : (
                    <Icon iconName="sample" />
                  )}
                </div>
              </SwiperSlide>
            ))}
          <SwiperSlide>
            <div className="flex rounded-[10px] overflow-hidden bg-inawo-green justify-center items-center gap-1 relative h-32 sm:h-48 md:h-64 my-2 md:my-4">
              <Link href={`/profile/${props.item?.id}`}>
                <a className="flex justify-center items-center w-full h-full gap-1">
                  <Icon iconName="chevronRight" />
                  <p className="font-light font-sfpro text-xs xs:text-sm text-white">
                    View <br /> Profile
                  </p>
                </a>
              </Link>
            </div>
          </SwiperSlide>
        </Swiper>
      ) : (
        <div className="h-36 sm:h-52 md:h-72" />
      )}
    </div>
  );
};

export default ServiceCarouselWithDescription;
